/**
 * ActionDetailPanel - right-side panel for editing a selected action's fields
 *
 * @author    Claude / contexagon GmbH
 */

import React from 'react';
import { getActionIcon, getActionLabel, getStartBorderColor, getStartLabel } from './actionTypes';


// ── Small form primitives ─────────────────────────────────────────────────────

function FieldRow({ label, children }) {
    return (
        <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 }}>
                {label}
            </div>
            {children}
        </div>
    );
}

const inputStyle = {
    width: '100%',
    padding: '6px 8px',
    border: '1px solid #ddd',
    borderRadius: 4,
    fontSize: 13,
    boxSizing: 'border-box',
    outline: 'none',
    backgroundColor: 'white',
};

const selectStyle = {
    ...inputStyle,
    cursor: 'pointer',
    appearance: 'none',
    backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'10\' height=\'6\'%3E%3Cpath d=\'M0 0l5 6 5-6z\' fill=\'%23999\'/%3E%3C/svg%3E")',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 8px center',
    paddingRight: 24,
};

function NumberWithSuffix({ value, suffix, onChange }) {
    return (
        <div style={{ display: 'flex', gap: 4 }}>
            <input
                type="number"
                min="0"
                step="any"
                value={value === undefined || value === null ? '' : value}
                onChange={e => onChange(e.target.value === '' ? 0 : Number(e.target.value))}
                style={{ ...inputStyle, flex: 1 }}
            />
            <div style={{
                padding: '6px 8px',
                border: '1px solid #ddd',
                borderRadius: 4,
                fontSize: 12,
                color: '#888',
                backgroundColor: '#f5f5f5',
                flexShrink: 0,
            }}>
                {suffix || 's'}
            </div>
        </div>
    );
}


// ── Text field handling ───────────────────────────────────────────────────────

function getTextValue(textField) {
    if (!textField) return { value: '', isTranslated: false };
    if (typeof textField === 'string') return { value: textField, isTranslated: false };
    if (typeof textField === 'object') {
        // Translated: { de: "...", en: "..." } — edit each language separately
        return { value: textField, isTranslated: true };
    }
    return { value: '', isTranslated: false };
}


// ── Type-specific field sections ──────────────────────────────────────────────

function TextActionFields({ fields, onFieldChange }) {
    const { value: textValue, isTranslated } = getTextValue(fields.text);

    if (isTranslated) {
        return (
            <FieldRow label="Text (mehrsprachig)">
                {Object.entries(textValue).map(([lang, text]) => (
                    <div key={lang} style={{ marginBottom: 6 }}>
                        <div style={{ fontSize: 10, color: '#aaa', marginBottom: 2 }}>{lang.toUpperCase()}</div>
                        <textarea
                            value={text || ''}
                            rows={3}
                            onChange={e => onFieldChange('text', { ...textValue, [lang]: e.target.value })}
                            style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }}
                        />
                    </div>
                ))}
            </FieldRow>
        );
    }

    return (
        <FieldRow label="Text">
            <textarea
                value={textValue}
                rows={5}
                onChange={e => onFieldChange('text', e.target.value)}
                style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }}
            />
        </FieldRow>
    );
}

function SceneActionFields({ fields }) {
    const sceneIds = fields.scene || [];
    if (!sceneIds.length) return null;
    return (
        <FieldRow label="Zielszene(n)">
            <div style={{ fontSize: 12, color: '#555', padding: '6px 8px', border: '1px solid #ddd', borderRadius: 4, backgroundColor: '#fafafa' }}>
                {Array.isArray(sceneIds) ? sceneIds.join(', ') : String(sceneIds)}
            </div>
            <div style={{ fontSize: 10, color: '#aaa', marginTop: 3 }}>
                Szenenzuordnung im Standard-Editor bearbeiten
            </div>
        </FieldRow>
    );
}

function MediaActionFields({ fields }) {
    const mediaIds = fields.medialist || [];
    if (!mediaIds.length && !fields.name && !fields.title) return null;
    return (
        <>
            {(fields.name || fields.title) && (
                <FieldRow label="Titel">
                    <div style={{ fontSize: 13, color: '#333' }}>{fields.name || fields.title}</div>
                </FieldRow>
            )}
            {mediaIds.length > 0 && (
                <FieldRow label="Medienobjekte">
                    <div style={{ fontSize: 12, color: '#555', padding: '6px 8px', border: '1px solid #ddd', borderRadius: 4, backgroundColor: '#fafafa' }}>
                        {Array.isArray(mediaIds) ? mediaIds.join(', ') : String(mediaIds)}
                    </div>
                    <div style={{ fontSize: 10, color: '#aaa', marginTop: 3 }}>
                        Medienzuordnung im Standard-Editor bearbeiten
                    </div>
                </FieldRow>
            )}
        </>
    );
}

function MenuActionFields({ fields, onFieldChange }) {
    return (
        <>
            {fields.slot !== undefined && (
                <FieldRow label="Slot">
                    <select
                        value={fields.slot || ''}
                        onChange={e => onFieldChange('slot', e.target.value)}
                        style={selectStyle}
                    >
                        <option value="A">A</option>
                        <option value="B">B</option>
                    </select>
                </FieldRow>
            )}
        </>
    );
}

function ControlActionFields({ fields }) {
    const command = fields.command || fields.controltype;
    if (!command) return null;
    return (
        <FieldRow label="Befehl">
            <div style={{ fontSize: 13, color: '#333', padding: '6px 8px', border: '1px solid #eee', borderRadius: 4, backgroundColor: '#fafafa', fontFamily: 'monospace' }}>
                {command}
            </div>
        </FieldRow>
    );
}

function NavigationActionFields({ fields }) {
    if (!fields.command) return null;
    return (
        <FieldRow label="Befehl">
            <div style={{ fontSize: 13, color: '#333', padding: '6px 8px', border: '1px solid #eee', borderRadius: 4, backgroundColor: '#fafafa', fontFamily: 'monospace' }}>
                {fields.command}
            </div>
        </FieldRow>
    );
}

function InteractionFields({ fields, onFieldChange }) {
    const { value: textValue, isTranslated } = getTextValue(fields.text);
    const options = Array.isArray(fields.options) ? fields.options : [];

    return (
        <>
            {/* Question text */}
            {isTranslated ? (
                <FieldRow label="Text (mehrsprachig)">
                    {Object.entries(textValue).map(([lang, text]) => (
                        <div key={lang} style={{ marginBottom: 6 }}>
                            <div style={{ fontSize: 10, color: '#aaa', marginBottom: 2 }}>{lang.toUpperCase()}</div>
                            <textarea
                                value={text || ''}
                                rows={3}
                                onChange={e => onFieldChange('text', { ...textValue, [lang]: e.target.value })}
                                style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }}
                            />
                        </div>
                    ))}
                </FieldRow>
            ) : (
                <FieldRow label="Text">
                    <textarea
                        value={textValue || ''}
                        rows={3}
                        onChange={e => onFieldChange('text', e.target.value)}
                        style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }}
                    />
                </FieldRow>
            )}

            {/* Options list */}
            {options.length > 0 && (
                <FieldRow label={`Optionen (${options.length})`}>
                    {options.map((option, i) => {
                        const f = option.fields || {};
                        const answer = typeof f.answer === 'string'
                            ? f.answer
                            : (f.answer && (f.answer.de || f.answer.en || Object.values(f.answer).find(v => v) || ''));
                        const reactionCount = Array.isArray(f.reactions) ? f.reactions.length : 0;
                        const conditionCount = Array.isArray(f.conditions) ? f.conditions.length : 0;
                        return (
                            <div key={i} style={{
                                padding: '7px 10px',
                                marginBottom: 4,
                                borderRadius: 5,
                                backgroundColor: '#f8f9ff',
                                border: '1px solid #e8eaf6',
                                fontSize: 12,
                            }}>
                                <div style={{ fontWeight: 600, color: '#333', marginBottom: (reactionCount + conditionCount) > 0 ? 3 : 0 }}>
                                    ◉ {answer || <span style={{ color: '#bbb', fontStyle: 'italic' }}>leer</span>}
                                </div>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    {reactionCount > 0 && (
                                        <span style={{ fontSize: 10, color: '#7986cb' }}>
                                            {reactionCount} Reaktion{reactionCount !== 1 ? 'en' : ''}
                                        </span>
                                    )}
                                    {conditionCount > 0 && (
                                        <span style={{ fontSize: 10, color: '#ef9a9a' }}>
                                            {conditionCount} Bed.
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                    <div style={{ fontSize: 10, color: '#aaa', marginTop: 3 }}>
                        Optionen im Standard-Editor bearbeiten
                    </div>
                </FieldRow>
            )}
        </>
    );
}

function ContentActionFields({ fields, referencedObjects }) {
    const ids = Array.isArray(fields.content) ? fields.content : [];
    if (ids.length === 0) return null;
    const obj = referencedObjects[ids[0]];
    const name = obj ? (obj.title || (obj.fields && (obj.fields.name || obj.fields.title))) : null;
    return (
        <FieldRow label="Inhalt">
            <div style={{ fontSize: 13, color: '#333', padding: '6px 8px', border: '1px solid #ddd', borderRadius: 4, backgroundColor: '#fafafa' }}>
                {name || <span style={{ color: '#bbb', fontStyle: 'italic' }}>{ids[0]}</span>}
            </div>
            {ids.length > 1 && (
                <div style={{ fontSize: 10, color: '#aaa', marginTop: 3 }}>
                    {ids.length} Inhalte gesamt
                </div>
            )}
            <div style={{ fontSize: 10, color: '#aaa', marginTop: 3 }}>
                Inhaltszuordnung im Standard-Editor bearbeiten
            </div>
        </FieldRow>
    );
}

function StateActionFields({ fields, referencedObjects }) {
    const method = fields.method;
    const value = fields.value;
    const propIds = Array.isArray(fields.property) ? fields.property : [];
    const propObj = propIds.length > 0 ? referencedObjects[propIds[0]] : null;
    const propName = propObj ? (propObj.title || (propObj.fields && propObj.fields.name)) : null;

    return (
        <>
            <FieldRow label="Eigenschaft">
                <div style={{ fontSize: 13, color: '#333', padding: '6px 8px', border: '1px solid #ddd', borderRadius: 4, backgroundColor: '#fafafa' }}>
                    {propName || (propIds[0] ? <span style={{ color: '#bbb', fontStyle: 'italic' }}>{propIds[0]}</span> : <span style={{ color: '#bbb', fontStyle: 'italic' }}>—</span>)}
                </div>
            </FieldRow>
            {method && (
                <FieldRow label="Methode">
                    <div style={{ fontSize: 13, color: '#333', padding: '6px 8px', border: '1px solid #eee', borderRadius: 4, backgroundColor: '#fafafa' }}>
                        {method === 'add' ? '+ addieren' : '← zuweisen'}
                    </div>
                </FieldRow>
            )}
            {value !== undefined && (
                <FieldRow label="Wert">
                    <div style={{ fontSize: 13, color: '#333', padding: '6px 8px', border: '1px solid #eee', borderRadius: 4, backgroundColor: '#fafafa' }}>
                        {String(value)}
                    </div>
                </FieldRow>
            )}
        </>
    );
}

function LightActionFields({ fields }) {
    const command = fields.command;
    const value = fields.commandvalue;
    if (!command && !value) return null;
    return (
        <>
            {command && (
                <FieldRow label="Lichtbefehl">
                    <div style={{ fontSize: 13, color: '#333', padding: '6px 8px', border: '1px solid #eee', borderRadius: 4, backgroundColor: '#fafafa', fontFamily: 'monospace' }}>
                        {command}
                    </div>
                </FieldRow>
            )}
            {value !== undefined && value !== '' && (
                <FieldRow label="Wert">
                    <div style={{ fontSize: 13, color: '#333', padding: '6px 8px', border: '1px solid #eee', borderRadius: 4, backgroundColor: '#fafafa' }}>
                        {String(value)}
                    </div>
                </FieldRow>
            )}
        </>
    );
}


// ── Main panel ────────────────────────────────────────────────────────────────

function ActionDetailPanel({ action, index, referencedObjects = {}, onUpdate, onClose }) {
    if (!action) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: '#ccc',
                fontSize: 13,
                textAlign: 'center',
                padding: 20,
            }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>👆</div>
                Aktion auswählen, um sie zu bearbeiten
            </div>
        );
    }

    const fields = action.fields || {};
    const type = action.type;
    const start = fields.start || 'initially';
    const startColor = getStartBorderColor(start);

    const updateField = (key, value) => {
        onUpdate(index, { ...fields, [key]: value });
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Panel header — left border shows Startzeitpunkt color */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '12px 16px',
                borderBottom: '1px solid #eee',
                borderLeft: '4px solid ' + startColor,
                flexShrink: 0,
            }}>
                <span style={{ fontSize: 20 }}>{getActionIcon(type)}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#333' }}>{getActionLabel(type)}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                        <span style={{ fontSize: 9, color: '#aaa' }}>Aktion {index + 1}</span>
                        <span style={{
                            fontSize: 9,
                            padding: '1px 6px',
                            borderRadius: 8,
                            backgroundColor: startColor + '20',
                            color: startColor,
                            fontWeight: 700,
                        }}>
                            {getStartLabel(start)}
                        </span>
                    </div>
                </div>
                <button
                    onClick={onClose}
                    style={{
                        border: 'none',
                        backgroundColor: 'transparent',
                        color: '#bbb',
                        cursor: 'pointer',
                        fontSize: 18,
                        lineHeight: 1,
                        padding: 4,
                        borderRadius: 4,
                    }}
                    title="Schließen"
                >
                    ×
                </button>
            </div>

            {/* Scrollable fields */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '14px 16px' }}>

                {/* Universal: start timing */}
                <FieldRow label="Startzeitpunkt">
                    <select
                        value={fields.start || 'initially'}
                        onChange={e => updateField('start', e.target.value)}
                        style={selectStyle}
                    >
                        <option value="initially">▶ Start (t=0)</option>
                        <option value="withprevious">‖ Parallel (gleiche Zeit)</option>
                        <option value="afterprevious">→ Danach (sequenziell)</option>
                    </select>
                </FieldRow>

                {/* Universal: delay */}
                <FieldRow label="Verzögerung">
                    <NumberWithSuffix
                        value={fields.delay}
                        suffix={fields['delay__suffix'] || 's'}
                        onChange={v => updateField('delay', v)}
                    />
                </FieldRow>

                {/* Universal: duration */}
                <FieldRow label="Dauer">
                    <NumberWithSuffix
                        value={fields.duration}
                        suffix={fields['duration__suffix'] || 's'}
                        onChange={v => updateField('duration', v)}
                    />
                </FieldRow>

                {/* Universal: active */}
                <FieldRow label="Aktiv">
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: '#444' }}>
                        <input
                            type="checkbox"
                            checked={fields.active !== false}
                            onChange={e => updateField('active', e.target.checked)}
                            style={{ width: 15, height: 15, cursor: 'pointer' }}
                        />
                        Aktion ausführen
                    </label>
                </FieldRow>

                {/* Divider */}
                <div style={{ borderTop: '1px solid #f0f0f0', margin: '4px 0 14px' }} />

                {/* Type-specific fields */}
                {type === 'textaction' && (
                    <TextActionFields fields={fields} onFieldChange={updateField} />
                )}
                {type === 'sceneaction' && (
                    <SceneActionFields fields={fields} />
                )}
                {type === 'mediaaction' && (
                    <MediaActionFields fields={fields} onFieldChange={updateField} />
                )}
                {type === 'menuaction' && (
                    <MenuActionFields fields={fields} onFieldChange={updateField} />
                )}
                {type === 'controlaction' && (
                    <ControlActionFields fields={fields} />
                )}
                {type === 'navigationaction' && (
                    <NavigationActionFields fields={fields} />
                )}
                {type === 'lightaction' && (
                    <LightActionFields fields={fields} />
                )}
                {type === 'interaction' && (
                    <InteractionFields fields={fields} onFieldChange={updateField} />
                )}
                {type === 'notificationaction' && (
                    <TextActionFields fields={fields} onFieldChange={updateField} />
                )}
                {type === 'contentaction' && (
                    <ContentActionFields fields={fields} referencedObjects={referencedObjects} />
                )}
                {type === 'stateaction' && (
                    <StateActionFields fields={fields} referencedObjects={referencedObjects} />
                )}

            </div>
        </div>
    );
}

export default ActionDetailPanel;
